import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from 'react'
import { createPortal } from 'react-dom'
import { Check, ChevronDown, Search, X } from 'lucide-react'
import {
  agruparOpciones,
  filtrarOpciones,
  siguienteActiva,
  type OpcionDeSelect,
} from '@/shared/lib/opcionesDeSelect'

export type { OpcionDeSelect }

/**
 * Select con búsqueda: se abre, se escribe para filtrar las opciones y se elige
 * con el mouse o con el teclado.
 *
 * Tiene dos estilos y hay que escoger el que corresponde a donde va:
 *
 *  - `formulario`: el de los campos de captura. Mismo fondo, alto y redondeo que
 *    un input de formulario, y el chevron separado del borde.
 *  - `listado`: el de las barras de filtros. Botón blanco con borde, igual que
 *    los filtros de almacén; con `todos` agrega la opción que limpia el filtro.
 *
 * Reemplaza al `<select>` nativo, que se ve distinto en cada sistema operativo y
 * no deja escribir. Para no perder lo que daba el nativo:
 *
 *  - `required` y `name` siguen funcionando con un formulario nativo: va un
 *    input oculto que lleva el valor, así el navegador frena el envío y avisa
 *    junto al campo.
 *  - `<label htmlFor>` enfoca el control, porque `id` va en el botón.
 *  - Los grupos (`group`) se pintan con su encabezado, como un `optgroup`.
 *  - El panel se monta en el `body`, así que no lo recorta un modal ni una
 *    tabla con scroll.
 *
 * `onChange` recibe el valor, no un evento. Donde había un manejador genérico
 * que lee `e.target.name`, se adapta con `comoEventoDeCambio`, que vive en
 * `shared/lib/opcionesDeSelect`.
 */
export interface SelectBuscableProps {
  variante: 'formulario' | 'listado'
  opciones: readonly OpcionDeSelect[]
  value: string
  onChange: (valor: string) => void
  /** Lo que se lee cuando no hay nada elegido (formulario). */
  placeholder?: string
  /** El nombre del filtro, que se lee cuando no hay nada elegido (listado). */
  etiqueta?: string
  /** Texto de la opción que limpia la selección, como «Todos» (listado). */
  todos?: string
  /** `contenido` se ajusta al texto; `completo` ocupa todo el ancho. */
  ancho?: 'completo' | 'contenido'
  name?: string
  id?: string
  required?: boolean
  disabled?: boolean
  /** Marca el campo como inválido: anillo rojo en el estilo de formulario. */
  error?: boolean
  /**
   * Muestra la X que quita la selección (deja el valor vacío). Por omisión sí;
   * con `false` no sale, para campos que no deben poder quedar sin elegir.
   * Tampoco sale sin nada elegido ni con el select deshabilitado.
   */
  canDelete?: boolean
  className?: string
  placeholderDeBusqueda?: string
  textoSinResultados?: string
  'aria-label'?: string
  'aria-labelledby'?: string
}

/** Lo que cabe en pantalla antes de que el panel se abra hacia arriba. */
const ALTO_MINIMO_ABAJO = 240
const ANCHO_MINIMO_DEL_PANEL = 208

interface Posicion {
  left: number
  width: number
  top?: number
  bottom?: number
  maxHeight: number
}

export default function SelectBuscable({
  variante,
  opciones,
  value,
  onChange,
  placeholder = 'Selecciona una opción',
  etiqueta = '',
  todos,
  ancho = 'completo',
  name,
  id,
  required = false,
  disabled = false,
  error = false,
  canDelete = true,
  className = '',
  placeholderDeBusqueda = 'Buscar…',
  textoSinResultados = 'Sin resultados',
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
}: SelectBuscableProps) {
  const idInterno = useId()
  const idDelBoton = id ?? `select-${idInterno}`
  const idDeLaLista = `lista-${idInterno}`

  const [abierto, setAbierto] = useState(false)
  const [consulta, setConsulta] = useState('')
  const [activo, setActivo] = useState(-1)
  const [posicion, setPosicion] = useState<Posicion | null>(null)

  const botonRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const busquedaRef = useRef<HTMLInputElement>(null)
  const opcionesRef = useRef<Map<number, HTMLElement>>(new Map())
  // La última búsqueda con la que se acomodó la opción activa.
  const consultaAcomodada = useRef('')

  // «Todos» es una opción más, la primera, con valor vacío.
  const todasLasOpciones = useMemo<OpcionDeSelect[]>(
    () => (todos !== undefined ? [{ value: '', label: todos }, ...opciones] : [...opciones]),
    [opciones, todos],
  )

  const visibles = useMemo(
    () => filtrarOpciones(todasLasOpciones, consulta),
    [todasLasOpciones, consulta],
  )
  const grupos = useMemo(() => agruparOpciones(visibles), [visibles])

  const elegida = todasLasOpciones.find((o) => o.value === value)
  // En el listado, sin selección se lee el nombre del filtro («Estatus»), no
  // «Todos»: así el botón dice qué se está filtrando.
  const textoDelBoton =
    variante === 'listado' && todos !== undefined && value === ''
      ? etiqueta || todos
      : (elegida?.label ?? (variante === 'listado' ? etiqueta : placeholder))
  const sinSeleccion = variante === 'formulario' && !elegida

  const cerrar = useCallback((devolverFoco: boolean) => {
    setAbierto(false)
    setConsulta('')
    if (devolverFoco) botonRef.current?.focus()
  }, [])

  const abrir = useCallback(
    (consultaInicial = '') => {
      if (disabled) return
      setConsulta(consultaInicial)
      consultaAcomodada.current = consultaInicial
      const lista = filtrarOpciones(todasLasOpciones, consultaInicial)
      const seleccionada = lista.findIndex((o) => o.value === value && !o.disabled)
      setActivo(seleccionada >= 0 ? seleccionada : siguienteActiva(lista, -1, 1))
      setAbierto(true)
    },
    [disabled, todasLasOpciones, value],
  )

  const elegir = useCallback(
    (opcion: OpcionDeSelect) => {
      if (opcion.disabled) return
      onChange(opcion.value)
      cerrar(true)
    },
    [onChange, cerrar],
  )

  // El panel se coloca a partir del botón y se vuelve a colocar si la pantalla
  // se mueve: va en el body, así que no sigue al botón por sí solo.
  const colocarPanel = useCallback(() => {
    const boton = botonRef.current
    if (!boton) return
    const caja = boton.getBoundingClientRect()
    const margen = 8
    const anchoDelPanel = Math.max(caja.width, ANCHO_MINIMO_DEL_PANEL)
    const left = Math.max(
      margen,
      Math.min(caja.left, window.innerWidth - anchoDelPanel - margen),
    )
    const abajo = window.innerHeight - caja.bottom - margen
    const arriba = caja.top - margen
    const haciaArriba = abajo < ALTO_MINIMO_ABAJO && arriba > abajo

    setPosicion(
      haciaArriba
        ? {
            left,
            width: anchoDelPanel,
            bottom: window.innerHeight - caja.top + 4,
            maxHeight: Math.min(360, arriba - 4),
          }
        : {
            left,
            width: anchoDelPanel,
            top: caja.bottom + 4,
            maxHeight: Math.min(360, abajo - 4),
          },
    )
  }, [])

  useLayoutEffect(() => {
    if (!abierto) return
    colocarPanel()
    window.addEventListener('resize', colocarPanel)
    window.addEventListener('scroll', colocarPanel, true)
    return () => {
      window.removeEventListener('resize', colocarPanel)
      window.removeEventListener('scroll', colocarPanel, true)
    }
  }, [abierto, colocarPanel])

  // El foco pasa al buscador cuando el panel ya está en pantalla —el buscador
  // existe hasta que se calcula la posición—. Si se abrió escribiendo sobre el
  // botón, el cursor queda después de lo que ya se escribió.
  const panelListo = abierto && posicion !== null
  useEffect(() => {
    const busqueda = busquedaRef.current
    if (!panelListo || !busqueda) return
    busqueda.focus()
    const final = busqueda.value.length
    busqueda.setSelectionRange?.(final, final)
  }, [panelListo])

  // Clic fuera cierra. El panel está en el body, así que se revisa contra los
  // dos nodos y no contra un contenedor.
  useEffect(() => {
    if (!abierto) return
    const alPresionar = (evento: MouseEvent) => {
      const objetivo = evento.target as Node
      if (botonRef.current?.contains(objetivo) || panelRef.current?.contains(objetivo)) return
      cerrar(false)
    }
    document.addEventListener('mousedown', alPresionar)
    return () => document.removeEventListener('mousedown', alPresionar)
  }, [abierto, cerrar])

  // Al escribir, la activa pasa a la primera que coincide. Solo cuando cambia lo
  // escrito: al abrir, la activa es la elegida y no hay que moverla.
  useEffect(() => {
    if (!abierto || consultaAcomodada.current === consulta) return
    consultaAcomodada.current = consulta
    setActivo(siguienteActiva(visibles, -1, 1))
  }, [consulta, abierto, visibles])

  // La activa se mantiene a la vista al moverse con el teclado.
  useEffect(() => {
    if (!abierto || activo < 0) return
    opcionesRef.current.get(activo)?.scrollIntoView?.({ block: 'nearest' })
  }, [activo, abierto])

  const alTeclearEnElBoton = (evento: KeyboardEvent<HTMLButtonElement>) => {
    if (disabled) return
    if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(evento.key)) {
      evento.preventDefault()
      abrir()
      return
    }
    // Escribir con el select cerrado lo abre y arranca la búsqueda.
    if (evento.key.length === 1 && !evento.ctrlKey && !evento.metaKey && !evento.altKey) {
      evento.preventDefault()
      abrir(evento.key)
    }
  }

  const alTeclearEnLaBusqueda = (evento: KeyboardEvent<HTMLInputElement>) => {
    switch (evento.key) {
      case 'ArrowDown':
        evento.preventDefault()
        setActivo((actual) => siguienteActiva(visibles, actual, 1))
        break
      case 'ArrowUp':
        evento.preventDefault()
        setActivo((actual) => siguienteActiva(visibles, actual, -1))
        break
      case 'Home':
        evento.preventDefault()
        setActivo(siguienteActiva(visibles, -1, 1))
        break
      case 'End':
        evento.preventDefault()
        setActivo(siguienteActiva(visibles, 0, -1))
        break
      case 'Enter':
        // Sin esto, Enter enviaría el formulario que contiene al select.
        evento.preventDefault()
        if (activo >= 0 && visibles[activo]) elegir(visibles[activo])
        break
      case 'Escape':
        evento.preventDefault()
        evento.stopPropagation()
        cerrar(true)
        break
      case 'Tab':
        cerrar(false)
        break
      default:
    }
  }

  const clasesDelBoton =
    variante === 'formulario'
      ? [
          'w-full flex items-center justify-between gap-2 pl-3 pr-3.5 py-2 rounded-md leading-[22px]',
          'bg-[#F6F9FB] text-left text-gray-900 transition',
          'focus:outline-none focus:ring-2 focus:ring-[#477FFA]',
          error ? 'ring-1 ring-red-400' : '',
        ]
      : [
          'inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-200',
          'bg-white text-sm text-gray-700 hover:bg-gray-50 transition-colors whitespace-nowrap',
          'focus:outline-none focus:ring-2 focus:ring-[#477FFA]',
          ancho === 'completo' ? 'w-full justify-between' : '',
        ]

  const puedeQuitar = canDelete && !disabled && value !== ''

  let indiceVisible = -1

  return (
    <div className={`relative ${variante === 'listado' && ancho === 'contenido' ? 'inline-block' : ''} ${className}`}>
      <button
        ref={botonRef}
        id={idDelBoton}
        type="button"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={abierto}
        aria-controls={abierto ? idDeLaLista : undefined}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        aria-invalid={error || undefined}
        aria-required={required || undefined}
        disabled={disabled}
        onClick={() => (abierto ? cerrar(false) : abrir())}
        onKeyDown={alTeclearEnElBoton}
        className={`select-buscable disabled:cursor-not-allowed disabled:opacity-60 ${clasesDelBoton.filter(Boolean).join(' ')}`}
      >
        {/*
          El texto se corta con puntos suspensivos en vez de desbordarse, y el
          `title` conserva el texto completo. El `min-w-0` es lo que deja que el
          `truncate` actúe dentro de un flex.
        */}
        <span
          className={`min-w-0 truncate ${sinSeleccion ? 'text-gray-400' : ''}`}
          title={textoDelBoton}
        >
          {textoDelBoton}
        </span>
        <ChevronDown
          className={`select-buscable-chevron w-4 h-4 flex-shrink-0 text-gray-400 ${
            puedeQuitar ? 'invisible' : ''
          }`}
          aria-hidden="true"
        />
      </button>

      {/* La X va fuera del botón —un botón dentro de otro no es válido— y encima,
          en el lugar del chevron, que se oculta mientras haya algo elegido. */}
      {puedeQuitar && (
        <button
          type="button"
          aria-label="Quitar selección"
          title="Quitar selección"
          onClick={() => {
            onChange('')
            botonRef.current?.focus()
          }}
          className={`absolute top-1/2 -translate-y-1/2 p-1 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-200/60 transition-colors ${
            variante === 'formulario' ? 'right-[11px]' : 'right-[13px]'
          }`}
        >
          <X className="w-3.5 h-3.5" aria-hidden="true" />
        </button>
      )}

      {/* El valor para un formulario nativo: `name` en el envío y `required` en
          la validación. Al enfocarlo —el navegador lo hace al marcarlo inválido—
          el foco pasa al botón. */}
      {(name || required) && (
        <input
          tabIndex={-1}
          aria-hidden="true"
          name={name}
          value={value}
          required={required}
          disabled={disabled}
          onChange={() => undefined}
          onFocus={() => botonRef.current?.focus()}
          className="select-buscable-nativo"
        />
      )}

      {abierto &&
        posicion &&
        createPortal(
          <div
            ref={panelRef}
            style={{
              position: 'fixed',
              left: posicion.left,
              top: posicion.top,
              bottom: posicion.bottom,
              width: posicion.width,
              maxHeight: posicion.maxHeight,
            }}
            className="z-[1000] flex flex-col bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden"
          >
            <div className="relative p-2 border-b border-gray-100 flex-shrink-0">
              <Search
                className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none"
                aria-hidden="true"
              />
              <input
                ref={busquedaRef}
                type="text"
                role="searchbox"
                aria-label={placeholderDeBusqueda}
                aria-controls={idDeLaLista}
                aria-activedescendant={activo >= 0 ? `${idDeLaLista}-${activo}` : undefined}
                autoComplete="off"
                value={consulta}
                onChange={(e) => setConsulta(e.target.value)}
                onKeyDown={alTeclearEnLaBusqueda}
                placeholder={placeholderDeBusqueda}
                className="w-full pl-8 pr-2 py-1.5 text-sm rounded-md text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-[#477FFA]"
              />
            </div>

            <div
              id={idDeLaLista}
              role="listbox"
              aria-labelledby={ariaLabelledBy}
              className="overflow-y-auto py-1"
            >
              {visibles.length === 0 ? (
                <p className="px-4 py-3 text-sm text-gray-400">{textoSinResultados}</p>
              ) : (
                grupos.map(({ group, items }) => (
                  <div key={group ?? 'sin-grupo'} role="group" aria-label={group}>
                    {group && (
                      <p className="px-4 pt-2 pb-1 text-xs font-semibold text-gray-400 uppercase tracking-wide">
                        {group}
                      </p>
                    )}
                    {items.map((opcion) => {
                      indiceVisible += 1
                      const indice = indiceVisible
                      const esElegida = opcion.value === value
                      const esActiva = indice === activo
                      return (
                        <div
                          key={opcion.value || 'vacio'}
                          id={`${idDeLaLista}-${indice}`}
                          ref={(nodo) => {
                            if (nodo) opcionesRef.current.set(indice, nodo)
                            else opcionesRef.current.delete(indice)
                          }}
                          role="option"
                          aria-selected={esElegida}
                          aria-disabled={opcion.disabled || undefined}
                          // mousedown, no click: así el buscador no pierde el foco
                          // antes de que se registre la elección.
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => elegir(opcion)}
                          onMouseEnter={() => !opcion.disabled && setActivo(indice)}
                          className={`flex items-start justify-between gap-2 px-4 py-2 text-sm ${
                            opcion.disabled
                              ? 'cursor-not-allowed text-gray-300'
                              : 'cursor-pointer'
                          } ${esActiva ? 'bg-gray-50' : ''} ${
                            esElegida
                              ? 'text-[#253579] font-medium'
                              : opcion.value === '' && todos !== undefined
                                ? 'text-gray-500'
                                : 'text-gray-700'
                          }`}
                        >
                          {/* Una opción larga hace salto de línea en vez de cortarse: en
                              la lista hay que poder leerla completa para elegirla. */}
                          <span className="min-w-0 break-words" title={opcion.label}>
                            {opcion.label}
                          </span>
                          {esElegida && (
                            <Check className="mt-0.5 w-4 h-4 flex-shrink-0" aria-hidden="true" />
                          )}
                        </div>
                      )
                    })}
                  </div>
                ))
              )}
            </div>
          </div>,
          document.body,
        )}
    </div>
  )
}
